import React, { useMemo, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { Box } from '@mui/system';
import { Dialog, IconButton, Typography } from '@mui/material';

import { DatePicker } from '@bestdoctor/spectrum-uikit/DatePicker';
import { MultiCombobox } from '@bestdoctor/spectrum-uikit/MultiCombobox';
import type { ComboboxOption } from '@bestdoctor/spectrum-uikit/Combobox';
import { format, parse, isValid } from 'date-fns';

import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { TableHistoryActions } from 'shared/modals/HistoryActionsModal/TableHistoryActions/TableHistoryActions';
import { modalTableHeaderStyle } from 'ui/modals/modalTable/modalTable';
import { modalTitleStyle } from 'ui/modals/modalGlobal';

import { HistoryAction, IEventTypes, PatientDataParams } from 'interfaces/patients/patientInterfaces';

import { eventTypes } from 'utils/constants';
import { isDateActual } from 'utils/dateHelpers';

interface HistoryActionsModalModalProps {
  open: boolean;
  patientData: PatientDataParams;
  historyActions: HistoryAction[] | [];
  isLoadingCompanyActions: boolean;
  hasNextPageCompanyActions: boolean;
  isFetchingNextPageCompanyActions: boolean;

  startDate: Date | null;
  handleStartDate: (value: Date | null) => void;
  endDate: Date | null;
  handleEndDate: (value: Date | null) => void;
  eventType: IEventTypes[];
  handleEventType: (value: IEventTypes[]) => void;
  orderingTableHistory: string;
  handleOrderingTableHistory: (value: string) => void;

  fetchNextPageCompanyActions: () => Promise<void>;
  handleClose: () => void;
}

export const HistoryActionsModal: React.FC<HistoryActionsModalModalProps> = ({
  open,
  patientData,
  historyActions,

  isLoadingCompanyActions,
  hasNextPageCompanyActions,
  isFetchingNextPageCompanyActions,

  startDate,
  handleStartDate,
  endDate,
  handleEndDate,
  eventType,
  handleEventType,
  orderingTableHistory,
  handleOrderingTableHistory,

  fetchNextPageCompanyActions,
  handleClose,
}) => {
  const queryClient = useQueryClient();

  // Преобразуем Date в строку формата dd.MM.yyyy для DatePicker
  const startDateString = startDate ? format(startDate, 'dd.MM.yyyy') : undefined;
  const endDateString = endDate ? format(endDate, 'dd.MM.yyyy') : undefined;

  // Spectrum DatePicker отдаёт "промежуточные" строки при вводе — держим отдельный input-state,
  // иначе ввод будет откатываться до последней валидной даты.
  const [startDateInput, setStartDateInput] = useState<string | undefined>(startDateString);
  const [endDateInput, setEndDateInput] = useState<string | undefined>(endDateString);

  useEffect(() => {
    setStartDateInput(startDateString);
  }, [startDateString]);

  useEffect(() => {
    setEndDateInput(endDateString);
  }, [endDateString]);

  // Преобразуем типы событий в формат ComboboxOption
  const eventTypeOptions: ComboboxOption<IEventTypes>[] = useMemo(
    () =>
      eventTypes.map((event) => ({
        label: event.title,
        value: event,
      })),
    [eventTypes]
  );

  // Находим текущие выбранные опции типов событий
  const selectedEventTypeOptions = useMemo(
    () =>
      eventTypeOptions.filter((option) =>
        eventType.some((event) => event.id === option.value.id)
      ),
    [eventTypeOptions, eventType]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxHeight: '500px',
          overflowY: 'scroll',
          maxWidth: '1356px',
          padding: '20px 24px 24px',
          borderRadius: '24px',
        },
      }}
    >
      <Box sx={modalTableHeaderStyle}>
        <Box sx={{ marginBottom: '32px' }}>
          <Typography variant="h3" sx={modalTitleStyle}>
            История операций&nbsp;
            {patientData.full_name && (
              <Box component="span" sx={{ color: 'rgba(62, 65, 77, 0.6)' }}>{`(${patientData.full_name})`}</Box>
            )}
          </Typography>
        </Box>
        <IconButton aria-label="close modal" sx={{ width: '40px', height: '40px' }} onClick={handleClose}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: '16px', marginBottom: '32px' }}>
        <style>
          {`
            [data-radix-popper-content-wrapper] {
              z-index: 1400 !important;
            }
          `}
        </style>
        <Box sx={{ width: '160px', flexShrink: 0 }}>
          <DatePicker
            mode="single"
            label="Период данных с"
            placeholder="Период данных с"
            value={startDateInput}
            onChange={(newValue) => {
              setStartDateInput(newValue || undefined);
              if (newValue) {
                try {
                  const parsedDate = parse(newValue, 'dd.MM.yyyy', new Date());
                  if (isValid(parsedDate) && isDateActual(parsedDate)) {
                    handleStartDate(parsedDate);
                    queryClient.invalidateQueries('dataCompanyActions');
                  }
                } catch {
                  // Если не удалось распарсить, игнорируем
                }
              } else {
                handleStartDate(null);
                queryClient.invalidateQueries('dataCompanyActions');
              }
            }}
            disabled={isLoadingCompanyActions}
            size="s"
            style={{ width: '100%' }}
          />
        </Box>

        <Box sx={{ width: '160px', flexShrink: 0 }}>
          <DatePicker
            mode="single"
            label="Период данных по"
            placeholder="Период данных по"
            value={endDateInput}
            onChange={(newValue) => {
              setEndDateInput(newValue || undefined);
              if (newValue) {
                try {
                  const parsedDate = parse(newValue, 'dd.MM.yyyy', new Date());
                  if (isValid(parsedDate) && isDateActual(parsedDate)) {
                    handleEndDate(parsedDate);
                    queryClient.invalidateQueries('dataCompanyActions');
                  }
                } catch {
                  // Если не удалось распарсить, игнорируем
                }
              } else {
                handleEndDate(null);
                queryClient.invalidateQueries('dataCompanyActions');
              }
            }}
            disabled={isLoadingCompanyActions}
            size="s"
            style={{ width: '100%' }}
          />
        </Box>

        <Box sx={{ width: '180px' }}>
          <MultiCombobox<IEventTypes>
            label="Тип события"
            placeholder="Тип события"
            options={eventTypeOptions}
            value={selectedEventTypeOptions.map((opt) => opt.value)}
            searchable
            clearable
            onChange={(values) => {
              handleEventType(values || []);
              queryClient.invalidateQueries('dataCompanyActions');
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          overflowX: 'scroll',
        }}
      >
        <TableHistoryActions
          historyActions={historyActions}
          isLoadingCompanyActions={isLoadingCompanyActions}
          hasNextPageCompanyActions={hasNextPageCompanyActions}
          isFetchingNextPageCompanyActions={isFetchingNextPageCompanyActions}
          fetchNextPageCompanyActions={fetchNextPageCompanyActions}
          orderingTableHistory={orderingTableHistory}
          handleOrderingTableHistory={handleOrderingTableHistory}
        />
      </Box>
    </Dialog>
  );
};
